import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { CurrencyService } from '@core/services/currency.service';
import { SpinnerService } from '@core/services/spinner.service';
import { BudgetDetail, FundingSourceTypes } from '@core/typings/budget.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { NewInKindItemAllocation } from '@features/funding-sources/funding-source-calculator/funding-source-calculator.component';
import { InKindService } from '@features/in-kind/in-kind.service';
import { PaymentProcessingService } from '@features/payment-processing/payment-processing.service';
import { SupportService } from '@features/support/support.service';
import { SystemTagsService } from '@features/system-tags/system-tags.service';
import { SystemTags } from '@features/system-tags/typings/system-tags.typing';
import { FormControlIcon, TypeSafeFormBuilder, TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { TrackingEventNames } from '@yourcause/common/heap';
import { I18nService } from '@yourcause/common/i18n';
import { YCModalComponent } from '@yourcause/common/modals';
import { Subscription } from 'rxjs';
import { AddInKindBudgetGroup, InKindCategoryGroup } from '../budget-add-in-kind-item/budget-add-in-kind-item.component';
import { BudgetService } from '../budget.service';

export interface CreateEditBudgetGroup {
  name: string;
  description: string;
  accountNumber: string;
  processor: ProcessingTypes;
  fundingSourceType: FundingSourceTypes;
  budgetTotal: number;
}
export interface CreateEditBudgetModalResponse extends CreateEditBudgetGroup {
  budgetTags: number[];
}
export enum CreateEditBudgetScenario {
  Standard = 1,
  Calculator = 2,
  AddInKindItem = 3,
  AddInKindCategory = 4
}

@Component({
    selector: 'gc-create-edit-budget-modal',
    templateUrl: './create-edit-budget-modal.component.html',
    styleUrls: ['./create-edit-budget-modal.component.scss'],
    standalone: false
})
export class CreateEditBudgetModalComponent extends YCModalComponent<
  CreateEditBudgetModalResponse
> implements OnInit, OnDestroy {
  @Input() budget: BudgetDetail;

  Buckets = SystemTags.Buckets;
  availableTags: TypeaheadSelectOption[] = [];
  tagsFormGroup: TypeSafeFormGroup<SystemTags.TagsFormGroup>;
  addingNewTag = false;
  isNew = true;
  scenario = CreateEditBudgetScenario.Standard;
  formGroup: TypeSafeFormGroup<CreateEditBudgetGroup>;
  processorOptions = this.paymentProcessingService.getProcessorOptions();
  hideFundingSources = this.clientSettingsService.hideFundingSources;
  newItemIsValid = false;
  newInKindItem: AddInKindBudgetGroup;
  newInKindItemAllocation: NewInKindItemAllocation;
  newCategory: InKindCategoryGroup;
  newCategoryId: number;
  totalFromCalculator = 0;
  modalHeader = '';
  primaryButtonText = '';
  primaryDisabled: boolean;
  rightIcons: FormControlIcon[] = [{
    icon: this.clientSettingsService.defaultCurrency,
    isStaticText: true
  }];
  typeOptions: TypeaheadSelectOption<FundingSourceTypes>[] = [{
    label: this.i18n.translate(
      'GLOBAL:textCash'
    ),
    value: FundingSourceTypes.DOLLARS
  }, {
    label: this.i18n.translate(
      'GLOBAL:textInKind',
      {},
      'In-Kind'
    ),
    value: FundingSourceTypes.UNITS
  }];
  calculatorDescription = this.i18n.translate(
    'BUDGET:textBudgetModalCalculatorDesc',
    {},
    `Select in-kind items and units to estimate the total cash value of your budget. If an item is not available, click "Add new item" to add a new one. The selected items and units are only used to determine an approximate budget total. They will not be attached to your budget.`
  );
  currencyData = this.currencyService.formattingData[
    this.clientSettingsService.defaultCurrency
  ];
  CreateEditBudgetScenario = CreateEditBudgetScenario;
  sub = new Subscription();
  accountNumbersHelpLink = this.hideFundingSources ?
    'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-faq-do-budgets-need-account-numbers.html' :
    'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-faq-do-complex-budgets-need-account-numbers.html';
  inKindHelpLink = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-create-simple-budgets-in-kind.html';


  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private clientSettingsService: ClientSettingsService,
    private budgetService: BudgetService,
    private i18n: I18nService,
    private paymentProcessingService: PaymentProcessingService,
    private spinnerService: SpinnerService,
    private currencyService: CurrencyService,
    private inKindService: InKindService,
    private tagsService: SystemTagsService,
    private supportService: SupportService
  ) {
    super();
  }

  get isCash () {
    if (this.formGroup) {
      return this.formGroup.value.fundingSourceType === FundingSourceTypes.DOLLARS;
    }

    return true;
  }

  get processorType () {
    return this.clientSettingsService.clientSettings.clientProcessingType;
  }

  get showProcessorSelect () {
    return this.processorType === ProcessingTypes.Both &&
      this.formGroup?.value.fundingSourceType === FundingSourceTypes.DOLLARS &&
      this.hideFundingSources;
  }

  get secondaryButtonText () {
    if (this.scenario === CreateEditBudgetScenario.Calculator) {
      return this.i18n.translate('common:textAddNewItem', {}, 'Add new item');
    }

    return '';
  }

  async ngOnInit () {
    this.spinnerService.startSpinner();
    this.isNew = !!this.budget ? !this.budget.id : true;
    if (this.hideFundingSources || this.isNew) {
      await this.inKindService.resolveInKind();
    }
    this.availableTags = this.tagsService.getTagOptions(SystemTags.Buckets.Budget);
    const response = this.budgetService.getBudgetHelpersWhenHidingSources(this.budget);
    this.formGroup = this.formBuilder.group({
      name: [
        this.budget?.name ?? '',
        [Validators.required, Validators.maxLength(50)]
      ],
      description: [this.budget?.description ?? '', Validators.required],
      accountNumber: this.budget?.accountNumber ?? '',
      processor: this.hideFundingSources ?
        [response.processor, Validators.required] :
        [null], // we don't capture this when not hiding funding sources
      fundingSourceType: this.hideFundingSources ?
        [response.fundingSourceType, Validators.required] :
        [null], // we don't capture this when not hiding funding sources
      budgetTotal: this.hideFundingSources ?
        [response.budgetTotal, [Validators.required, Validators.min(response.minimumBudgetTotal)]] :
        [null] // we don't capture this when not hiding funding sources
    });
    const budgetTags = this.budgetService.budgetTagsMap[this.budget?.id] ?? [];
    const tags = budgetTags.map((tag) => tag.id);
    this.tagsFormGroup = this.formBuilder.group<SystemTags.TagsFormGroup>({
      tags: [tags],
      newTagName: ''
    });
    this.setScenario(CreateEditBudgetScenario.Standard);
    this.sub.add(this.formGroup.statusChanges.subscribe(() => {
      this.setModalAttrs();
    }));
    this.spinnerService.stopSpinner();
  }

  setScenario (scenario: CreateEditBudgetScenario) {
    this.scenario = scenario;
    this.setModalAttrs();
  }

  setModalAttrs () {
    switch (this.scenario) {
      case CreateEditBudgetScenario.Standard:
        if (this.budget?.isClosed) {
          this.modalHeader = this.i18n.translate('BUDGET:hdrViewBudget', {}, 'View Budget');
          this.primaryButtonText = this.i18n.translate('common:textClose', {}, 'Close');
        } else {
          this.modalHeader = this.i18n.translate(
            !this.isNew ? 'BUDGET:textEditBudget' : 'BUDGET:hdrCreateBudget',
            {},
            !this.isNew ? 'Edit Budget' : 'Create Budget'
          );
          if (this.hideFundingSources) {
            this.primaryButtonText = this.i18n.translate('common:btnSave', {}, 'Save');
          } else {
            this.primaryButtonText = this.i18n.translate(
              !this.isNew ? 'common:btnSave' : 'common:btnNext',
              {},
              !this.isNew ? 'Save' : 'Next'
            );
          }
        }
        this.primaryDisabled = this.formGroup.invalid || this.addingNewTag;
        break;
      case CreateEditBudgetScenario.Calculator:
        this.modalHeader = this.i18n.translate(
          'BUDGET:hdrCalculateInKindBudgetTotal',
          {},
          'Calculate In-Kind Budget Total'
        );
        this.primaryButtonText = this.i18n.translate(
          'common:textUpdateBudgetTotal',
          {},
          'Update budget total'
        );
        this.primaryDisabled = this.totalFromCalculator <= 0 || this.addingNewTag;
        break;
      case CreateEditBudgetScenario.AddInKindItem:
        this.modalHeader = this.i18n.translate(
          'GLOBAL:hdrAddItem',
          {},
          'Add Item'
        );
        this.primaryButtonText = this.i18n.translate(
          'common:linkCreate',
          {},
          'Create'
        );
        this.primaryDisabled = !this.newItemIsValid || this.addingNewTag;
        break;
      case CreateEditBudgetScenario.AddInKindCategory:
        this.modalHeader = this.i18n.translate(
          'common:hdrAddNewInKindCategory',
          {},
          'Add New In-Kind Category'
        );
        this.primaryButtonText = this.i18n.translate(
          'common:linkCreate',
          {},
          'Create'
        );
        this.primaryDisabled = !this.newItemIsValid || this.addingNewTag;
        break;
    }
  }

  onShowCalculator () {
    this.setScenario(CreateEditBudgetScenario.Calculator);
  }

  async onPrimaryClick () {
    if (this.budget?.isClosed) {
      this.closeModal.emit();
    } else {
      switch (this.scenario) {
        case CreateEditBudgetScenario.Standard:
          const value: CreateEditBudgetModalResponse = {
            ...this.formGroup.value,
            budgetTags: this.tagsFormGroup.value.tags
          };
          this.closeModal.emit(value);
          break;
        case CreateEditBudgetScenario.Calculator:
          this.setScenario(CreateEditBudgetScenario.Standard);
          this.formGroup.get('budgetTotal').setValue(this.totalFromCalculator);
          break;
        case CreateEditBudgetScenario.AddInKindItem:
          const id = await this.inKindService.addOrEditCategoryItem(
            this.newInKindItem.categoryId,
            {
              name: this.newInKindItem.itemName,
              units: this.newInKindItem.units,
              value: this.newInKindItem.value,
              identification: this.newInKindItem.identification
            },
            false
          );
          if (!!id) {
            this.newInKindItemAllocation = {
              id,
              allocatedUnits: this.newInKindItem.allocatedUnits || 0,
              identification: this.newInKindItem.identification,
              categoryId: this.newInKindItem.categoryId
            };
          }
          this.setScenario(CreateEditBudgetScenario.Calculator);
          break;
        case CreateEditBudgetScenario.AddInKindCategory:
          this.newCategoryId = await this.inKindService.createCategory(
            this.newCategory.name,
            this.newCategory.type,
            this.newCategory.defaultLang
          );
          this.setScenario(CreateEditBudgetScenario.AddInKindItem);
          break;
      }
    }
  }

  onSecondaryClick () {
    // Secondary is only used for Adding a new in-kind item
    this.onAddNewItem();
  }

  onCancel () {
    switch (this.scenario) {
      case CreateEditBudgetScenario.Standard:
        this.closeModal.emit();
        break;
      case CreateEditBudgetScenario.Calculator:
        this.setScenario(CreateEditBudgetScenario.Standard);
        break;
      case CreateEditBudgetScenario.AddInKindItem:
        this.setScenario(CreateEditBudgetScenario.Calculator);
        break;
      case CreateEditBudgetScenario.AddInKindCategory:
        this.setScenario(CreateEditBudgetScenario.AddInKindItem);
        break;
    }
  }

  onAddNewItem () {
    this.setScenario(CreateEditBudgetScenario.AddInKindItem);
  }

  onAddingInKindCategory (addingInKindCategory: boolean) {
    const scenario = addingInKindCategory ?
      CreateEditBudgetScenario.AddInKindCategory :
      CreateEditBudgetScenario.AddInKindItem;
    this.setScenario(scenario);
  }

  onNewInKindItemChange (inKindItem: AddInKindBudgetGroup) {
    this.newInKindItem = inKindItem;
  }

  onCategoryChange (category: InKindCategoryGroup) {
    this.newCategory = category;
  }

  onTotalCalculatorChange (total: number) {
    this.totalFromCalculator = total;
    this.setModalAttrs();
  }

  onNewItemValidChange (valid: boolean) {
    this.newItemIsValid = valid;
    this.setModalAttrs();
  }

  trackClick (link: string) {
    this.supportService.trackClick(TrackingEventNames.HelpLink, link)
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
